import PublicationText from "@/components/feature/PublicationText";
import type { Publication } from "@/lib/types";

interface FieldRowProps {
  label: string;
  value: string | null;
  searchQuery?: string;
  valueClassName?: string;
}

function FieldRow({ label, value, searchQuery = "", valueClassName = "" }: FieldRowProps) {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  return (
    <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-[4.5rem_1fr] sm:gap-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400 sm:pt-0.5">
        {label}
      </dt>
      <dd className={`text-sm leading-relaxed text-gray-600 ${valueClassName}`}>
        <PublicationText text={trimmed} searchQuery={searchQuery} />
      </dd>
    </div>
  );
}

function formatJournalWithCitation(
  journal: string | null,
  citation: string | null,
): string | null {
  const journalText = journal?.trim() ?? "";
  const citationText = citation?.trim() ?? "";

  if (!journalText && !citationText) return null;
  if (!citationText) return journalText;
  if (!journalText) return citationText;

  return `${journalText}, ${citationText}`;
}

interface PublicationCardProps {
  publication: Publication;
  searchQuery?: string;
}

export default function PublicationCard({
  publication,
  searchQuery = "",
}: PublicationCardProps) {
  const journalWithCitation = formatJournalWithCitation(
    publication.journal,
    publication.citation,
  );

  return (
    <article className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:border-[#1977cc]/25 hover:shadow-md md:p-6">
      <h3 className="mb-3 text-base font-semibold leading-snug text-[#2c4964] md:text-lg">
        <PublicationText text={publication.title} searchQuery={searchQuery} />
      </h3>

      <dl className="space-y-2.5 border-t border-gray-100 pt-3">
        <FieldRow label="Authors" value={publication.authors} searchQuery={searchQuery} />
        <FieldRow
          label="Journal"
          value={journalWithCitation}
          searchQuery={searchQuery}
          valueClassName="text-[#1977cc]/90"
        />
      </dl>
    </article>
  );
}
